import { SpaceTimeClient, type AuthoritativeMapDefinition } from '../spacetime/SpaceTimeClient';
import { type AuthoredBiomeMetadata, type PathBorderContinuationRecord, type PathCellRecord, type PathLayerRecord, type SpawnPoint } from '../spacetime/types';
import { canonicalizePathTileLayers, resolveCanonicalAtlasId } from './pathAutotiler/pathTileLayers';
import { canonicalizePathBorderContinuations } from './pathAutotiler/pathBorderContinuations';
import {
    type MapDefinition,
    type MapObjectPlacement,
    type BiomeRegion,
    type BiomeType,
    type TileData,
    type TerrainType,
    type PortalData,
    buildDemoMap,
} from '../model/gamestate/GameState';
import { loadInlineRenderAsset } from './renderAssetLoader';
import { parsePersistedMapObjects } from '../maps/mapObjectPersistence';

const TILE_FLOOR = 0;
const TILE_WALL = 1;
const TILE_PORTAL = 2;
const TILE_PATH = 3;
const BIOME_DEFAULT = 0;
const BIOME_GRASS = 1;
const BIOME_SAND = 2;
const BIOME_WATER = 3;
const BIOME_DIRT = 4;
const BIOME_GRASSY_DIRT = 5;
const BIOME_BRICK = 6;
const BIOME_BRICK2 = 7;
const BIOME_COBBLESTONE_PATH = 8;
const BIOME_REALISTIC_GRASS = 9;
const BIOME_REALISTIC_GRASS2 = 10;
const BIOME_REALISTIC_FLOOR = 11;
const BIOME_REALISTIC_WATER = 12;
const BIOME_REALISTIC_DIRT = 13;
const BIOME_SOFT_GRASS = 14;

export async function loadClientMapDefinition(
    client: SpaceTimeClient,
    cache: Map<string, MapDefinition>,
    mapId: string
): Promise<MapDefinition> {
    const cached = cache.get(mapId);
    if (cached) {
        console.log('[MAP CACHE] hit', { mapId, width: cached.width, height: cached.height, mapKind: cached.mapKind });
        return cached;
    }

    const authoritative = await client.maps.getMapDefinition(mapId);
    if (!authoritative) {
        console.warn(`[CLIENT] Missing authoritative map ${mapId}, falling back to demo map.`);
        const fallback = buildFallbackMap(mapId);
        cache.set(mapId, fallback);
        return fallback;
    }

    const converted = await convertAuthoritativeMapDefinition(authoritative);

    cache.set(mapId, converted);
    return converted;
}

async function convertAuthoritativeMapDefinition(source: AuthoritativeMapDefinition): Promise<MapDefinition> {
    const visibilityModeRaw = (source.rules as { visibilityMode?: unknown } | null | undefined)?.visibilityMode;
    const visibilityMode =
        visibilityModeRaw === 'full_map'
            ? 'full_map'
            : source.mapKind === 'town'
                ? 'full_map'
                : 'radius';
    if (source.payload.type === 'procedural') {
        console.warn('[MAP FETCH] procedural payload received', { mapId: source.mapId, generatorKey: source.payload.generatorKey });
        return generateProceduralMap(source.payload.generatorKey, source.width, source.height, source.spawnPoints);
    }

    const pathLayer = parsePathLayerValues(source.rules, source.width, source.height);
    const pathLayers = parsePathLayers(source.render, source.gridType ?? 'hex', source.width, source.height);
    const pathBorderContinuations = parsePathBorderContinuations(source.render, source.gridType ?? 'hex', source.width, source.height);
    const biomeStorage = parseBiomeStorage(source.biomeMetadata, source.width, source.height);
    const objects = parseMapObjects(source.objects, source.width, source.height);
    const authoredTileMap = await loadAuthoredTileMapFromRender(source.mapId, source.render, source.width, source.height);

    const tiles: TileData[][] = [];
    const expected = source.width * source.height;
    if (source.payload.tiles.length < expected) {
        console.warn(`[CLIENT] Map ${source.mapId} tile blob shorter than expected (${source.payload.tiles.length} < ${expected})`);
    }

    for (let y = 0; y < source.height; y += 1) {
        const row: TileData[] = [];
        for (let x = 0; x < source.width; x += 1) {
            const idx = y * source.width + x;
            const value = source.payload.tiles[idx] ?? TILE_WALL;
            const tileInfo = parseTileValue(value);
            const fallbackBiome = parseBiomeValue(source.payload.biomes[idx] ?? BIOME_DEFAULT);
            const biome = biomeStorage.hasMetadata
                ? resolveBiomeFromSparseStorage(
                    biomeStorage.defaultBiome,
                    biomeStorage.biomeRegions,
                    biomeStorage.biomeOverrides,
                    x,
                    y,
                    fallbackBiome,
                )
                : fallbackBiome;
            row.push({
                x,
                y,
                terrain: tileInfo.terrain,
                walkable: tileInfo.walkable,
                biome,
                pathValue: pathLayer[idx] ?? 0,
            });
        }
        tiles.push(row);
    }

    const portals = source.portals.map((portal) => ({
        x: portal.x,
        y: portal.y,
        destMapId: portal.destMapId,
        destX: portal.destX,
        destY: portal.destY,
        destMapKind: portal.destMapKind,
    }));

    return {
        name: source.name,
        notes: source.notes ?? null,
        width: source.width,
        height: source.height,
        mapKind: source.mapKind ?? 'overworld',
        visibilityMode,
        requireFacingForMove: source.mapKind === 'dungeon',
        tiles,
        portals,
        objects,
        spawnPoints: source.spawnPoints,
        gridType: source.gridType ?? 'hex',
        defaultBiome: biomeStorage.defaultBiome,
        biomeRegions: biomeStorage.biomeRegions,
        biomeOverrides: biomeStorage.biomeOverrides,
        pathLayer,
        pathLayers,
        pathBorderContinuations,
        authoredTileMap,
    };
}

function parseTileValue(value: number): { terrain: TerrainType; walkable: boolean } {
    switch (value) {
        case TILE_FLOOR:
            return { terrain: 'floor', walkable: true };
        case TILE_PATH:
            return { terrain: 'path', walkable: true };
        case TILE_PORTAL:
            return { terrain: 'portal', walkable: true };
        case TILE_WALL:
        default:
            return { terrain: 'wall', walkable: false };
    }
}

function parseBiomeValue(value: number): BiomeType {
    switch (value) {
        case BIOME_GRASS:
            return 'grass';
        case BIOME_SAND:
            return 'sand';
        case BIOME_WATER:
            return 'water';
        case BIOME_DIRT:
            return 'dirt';
        case BIOME_GRASSY_DIRT:
            return 'grassy_dirt';
        case BIOME_SOFT_GRASS:
            return 'soft_grass';
        case BIOME_BRICK:
            return 'brick';
        case BIOME_BRICK2:
            return 'brick2';
        case BIOME_COBBLESTONE_PATH:
            return 'cobblestone_path';
        case BIOME_REALISTIC_GRASS:
            return 'realistic_grass';
        case BIOME_REALISTIC_GRASS2:
            return 'realistic_grass2';
        case BIOME_REALISTIC_FLOOR:
            return 'realistic_floor';
        case BIOME_REALISTIC_WATER:
            return 'realistic_water';
        case BIOME_REALISTIC_DIRT:
            return 'realistic_dirt';
        case BIOME_DEFAULT:
        default:
            return 'default';
    }
}

function parsePathLayerValues(rules: unknown, width: number, height: number): number[] {
    const expected = width * height;
    if (!rules || typeof rules !== 'object') {
        return new Array(expected).fill(0);
    }
    const rulesObj = rules as Record<string, unknown>;
    const pathLayerRaw = rulesObj.pathLayer;
    if (!pathLayerRaw || typeof pathLayerRaw !== 'object') {
        return new Array(expected).fill(0);
    }
    const valuesRaw = (pathLayerRaw as { values?: unknown }).values;
    const values = Array.isArray(valuesRaw) ? valuesRaw : [];
    const normalized = new Array<number>(expected).fill(0);
    for (let index = 0; index < expected; index += 1) {
        const raw = values[index];
        normalized[index] = typeof raw === 'number' && Number.isFinite(raw) ? Math.max(0, Math.floor(raw)) : 0;
    }
    return normalized;
}

function parseMapObjects(objectsRaw: unknown, width: number, height: number): MapObjectPlacement[] {
    return parsePersistedMapObjects(objectsRaw, width, height);
}

function parsePathBorderContinuations(
    render: unknown,
    gridType: string,
    width: number,
    height: number,
): PathBorderContinuationRecord[] {
    if (!render || typeof render !== 'object') {
        return [];
    }
    const rawTiles = (render as { pathBorderContinuations?: unknown }).pathBorderContinuations;
    if (!Array.isArray(rawTiles)) {
        return [];
    }
    return canonicalizePathBorderContinuations(
        gridType,
        width,
        height,
        rawTiles
            .filter((entry): entry is PathBorderContinuationRecord => (
                typeof entry === 'object'
                && entry !== null
                && typeof (entry as PathBorderContinuationRecord).atlasId === 'string'
                && typeof (entry as PathBorderContinuationRecord).x === 'number'
                && typeof (entry as PathBorderContinuationRecord).y === 'number'
            ))
            .map((entry) => ({
                atlasId: entry.atlasId.trim(),
                x: Math.trunc(entry.x),
                y: Math.trunc(entry.y),
            })),
    );
}

function parsePathLayers(
    render: unknown,
    gridType: string,
    width: number,
    height: number,
): import('../model/gamestate/GameState').PathLayer[] {
    if (!render || typeof render !== 'object') {
        return [];
    }
    const rawLayers = (render as { pathLayers?: unknown }).pathLayers;
    if (!Array.isArray(rawLayers)) {
        return [];
    }
    const parsedLayers = rawLayers
        .filter((entry): entry is PathLayerRecord =>
            typeof entry === 'object'
            && entry !== null
            && typeof (entry as PathLayerRecord).layerId === 'string'
            && typeof (entry as PathLayerRecord).atlasId === 'string'
            && typeof (entry as PathLayerRecord).width === 'number'
            && typeof (entry as PathLayerRecord).height === 'number'
            && Array.isArray((entry as PathLayerRecord).data)
        )
        .map((entry) => {
            const expected = width * height;
            const normalized = new Array<import('../model/gamestate/GameState').PathCell | null>(expected).fill(null);
            for (let index = 0; index < expected; index += 1) {
                const value = entry.data[index] as PathCellRecord | null | undefined;
                if (!value || typeof value !== 'object') {
                    normalized[index] = null;
                    continue;
                }
                if (value.mode === 'autotile') {
                    normalized[index] = {
                        mode: 'autotile',
                        surfaceTerrain: value.surfaceTerrain === 'wall'
                            || value.surfaceTerrain === 'portal'
                            || value.surfaceTerrain === 'path'
                            || value.surfaceTerrain === 'floor'
                            ? value.surfaceTerrain
                            : undefined,
                    };
                    continue;
                }
                if (value.mode !== 'fixed' || typeof value.variantId !== 'string') {
                    normalized[index] = null;
                    continue;
                }
                normalized[index] = {
                    mode: 'fixed',
                    variantId: value.variantId.trim(),
                    surfaceTerrain: value.surfaceTerrain === 'wall'
                        || value.surfaceTerrain === 'portal'
                        || value.surfaceTerrain === 'path'
                        || value.surfaceTerrain === 'floor'
                        ? value.surfaceTerrain
                        : undefined,
                    flipHorizontal: value.flipHorizontal === true,
                    flipVertical: value.flipVertical === true,
                    rotationSteps: typeof value.rotationSteps === 'number' && Number.isFinite(value.rotationSteps)
                        ? Math.trunc(value.rotationSteps)
                        : 0,
                };
            }
            const atlasId = resolveCanonicalAtlasId({
                atlasId: entry.atlasId,
                layerId: entry.layerId,
            }) ?? entry.atlasId;
            return {
                layerId: entry.layerId,
                atlasId,
                width,
                height,
                data: normalized,
            };
        });
    return canonicalizePathTileLayers(width, height, parsedLayers, gridType);
}

function generateProceduralMap(
    _generatorKey: string,
    width: number,
    height: number,
    spawnPoints: Record<string, SpawnPoint> = {},
): MapDefinition {
    const tiles: TileData[][] = [];
    for (let y = 0; y < height; y += 1) {
        const row: TileData[] = [];
        for (let x = 0; x < width; x += 1) {
            row.push({ x, y, terrain: 'floor', walkable: true });
        }
        tiles.push(row);
    }
    return {
        width,
        height,
        mapKind: 'overworld',
        visibilityMode: 'radius',
        requireFacingForMove: false,
        tiles,
        portals: [],
        objects: [],
        spawnPoints: Object.keys(spawnPoints).length > 0
            ? spawnPoints
            : { default: { x: Math.floor(width / 2), y: Math.floor(height / 2) } },
        gridType: 'hex',
        defaultBiome: 'default',
        biomeRegions: [],
        biomeOverrides: {},
        pathLayer: new Array(width * height).fill(0),
        pathLayers: [],
        pathBorderContinuations: [],
        authoredTileMap: null,
    };
}

function buildFallbackMap(_mapId: string): MapDefinition {
    return buildDemoMap(10, 8);
}

function parseBiomeStorage(
    biomeMetadata: AuthoredBiomeMetadata | null | undefined,
    width: number,
    height: number,
): {
    hasMetadata: boolean;
    defaultBiome: BiomeType;
    biomeRegions: BiomeRegion[];
    biomeOverrides: Record<string, BiomeType>;
} {
    if (!biomeMetadata || typeof biomeMetadata !== 'object') {
        return {
            hasMetadata: false,
            defaultBiome: 'default',
            biomeRegions: [],
            biomeOverrides: {},
        };
    }

    const defaultBiome = normalizeBiomeId(biomeMetadata.defaultBiome);
    const biomeRegions = Array.isArray(biomeMetadata.biomeRegions)
        ? biomeMetadata.biomeRegions.flatMap((region) => {
            if (!region || typeof region !== 'object') {
                return [];
            }
            const record = region as Record<string, unknown>;
            const x = toInt(record.x);
            const y = toInt(record.y);
            const regionWidth = toInt(record.width);
            const regionHeight = toInt(record.height);
            if (
                x === null
                || y === null
                || regionWidth === null
                || regionHeight === null
                || x < 0
                || y < 0
                || regionWidth <= 0
                || regionHeight <= 0
                || x + regionWidth > width
                || y + regionHeight > height
            ) {
                return [];
            }
            return [{
                x,
                y,
                width: regionWidth,
                height: regionHeight,
                biome: normalizeBiomeId(record.biome),
            }];
        })
        : [];

    const biomeOverrides: Record<string, BiomeType> = {};
    if (biomeMetadata.biomeOverrides && typeof biomeMetadata.biomeOverrides === 'object' && !Array.isArray(biomeMetadata.biomeOverrides)) {
        for (const [key, value] of Object.entries(biomeMetadata.biomeOverrides)) {
            const [xRaw, yRaw] = key.split(',');
            const x = Number.parseInt(xRaw ?? '', 10);
            const y = Number.parseInt(yRaw ?? '', 10);
            if (!Number.isInteger(x) || !Number.isInteger(y) || x < 0 || y < 0 || x >= width || y >= height) {
                continue;
            }
            biomeOverrides[`${x},${y}`] = normalizeBiomeId(value);
        }
    }

    return {
        hasMetadata: true,
        defaultBiome,
        biomeRegions,
        biomeOverrides,
    };
}

function resolveBiomeFromSparseStorage(
    defaultBiome: BiomeType,
    biomeRegions: readonly BiomeRegion[],
    biomeOverrides: Readonly<Record<string, BiomeType>>,
    x: number,
    y: number,
    fallbackBiome: BiomeType,
): BiomeType {
    const override = biomeOverrides[`${x},${y}`];
    if (typeof override === 'string' && override.length > 0) {
        return override;
    }
    for (const region of biomeRegions) {
        if (x >= region.x && x < region.x + region.width && y >= region.y && y < region.y + region.height) {
            return region.biome;
        }
    }
    return defaultBiome === 'default' ? fallbackBiome : defaultBiome;
}

function toInt(value: unknown): number | null {
    return typeof value === 'number' && Number.isFinite(value) ? Math.trunc(value) : null;
}

function normalizeBiomeId(value: unknown): BiomeType {
    if (typeof value !== 'string') {
        return 'default';
    }
    const biome = value.trim().toLowerCase();
    return biome.length > 0 ? biome : 'default';
}

async function loadAuthoredTileMapFromRender(
    mapId: string,
    render: unknown,
    width: number,
    height: number,
): Promise<MapDefinition['authoredTileMap']> {
    if (!render || typeof render !== 'object') {
        return null;
    }
    const renderObj = render as {
        displayTileWidth?: unknown;
        displayTileHeight?: unknown;
        imagePath?: unknown;
        tileWidth?: unknown;
        tileHeight?: unknown;
        columns?: unknown;
        imageWidth?: unknown;
        imageHeight?: unknown;
        directTileImages?: unknown;
        layers?: unknown;
    };
    if (
        typeof renderObj.tileWidth !== 'number'
        || typeof renderObj.tileHeight !== 'number'
        || !Array.isArray(renderObj.layers)
        || (
            typeof renderObj.imagePath !== 'string'
            && (typeof renderObj.directTileImages !== 'object' || renderObj.directTileImages === null)
        )
        || (
            typeof renderObj.imagePath === 'string'
            && (
                typeof renderObj.columns !== 'number'
                || typeof renderObj.imageWidth !== 'number'
                || typeof renderObj.imageHeight !== 'number'
            )
        )
    ) {
        return null;
    }

    const authoredTileMap = await loadInlineRenderAsset({
        imagePath: renderObj.imagePath,
        tileWidth: renderObj.tileWidth,
        tileHeight: renderObj.tileHeight,
        columns: typeof renderObj.columns === 'number' ? renderObj.columns : undefined,
        imageWidth: typeof renderObj.imageWidth === 'number' ? renderObj.imageWidth : undefined,
        imageHeight: typeof renderObj.imageHeight === 'number' ? renderObj.imageHeight : undefined,
        directTileImages:
            typeof renderObj.directTileImages === 'object' && renderObj.directTileImages !== null
                ? renderObj.directTileImages as never
                : undefined,
        layers: renderObj.layers as never,
        displayTileWidth:
            typeof renderObj.displayTileWidth === 'number' && Number.isFinite(renderObj.displayTileWidth)
                ? renderObj.displayTileWidth
                : undefined,
        displayTileHeight:
            typeof renderObj.displayTileHeight === 'number' && Number.isFinite(renderObj.displayTileHeight)
                ? renderObj.displayTileHeight
                : undefined,
    });

    const firstLayer = authoredTileMap.layers[0];
    if (firstLayer && (firstLayer.width !== width || firstLayer.height !== height)) {
        throw new Error(
            `Render asset for map '${mapId}' is ${firstLayer.width}x${firstLayer.height}, expected ${width}x${height}.`
        );
    }

    return authoredTileMap;
}
